import { useEffect } from "react";

interface PageHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  keywords?: string;
  noindex?: boolean;
  pageType?: 'website' | 'article' | 'service' | 'organization';
  structuredData?: object[];
  breadcrumbs?: object;
}

export function PageHead({ 
  title, 
  description, 
  canonical, 
  ogImage,
  keywords,
  noindex = false,
  pageType = 'website',
  structuredData = [],
  breadcrumbs
}: PageHeadProps) {
  useEffect(() => {
    // SEO Title (≤60 chars for optimal display)
    document.title = title;
    
    // Meta description (≤160 chars for optimal display)
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }

    // Keywords meta tag
    if (keywords) {
      let keywordsMeta = document.querySelector('meta[name="keywords"]');
      if (!keywordsMeta) {
        keywordsMeta = document.createElement('meta');
        keywordsMeta.setAttribute('name', 'keywords');
        document.head.appendChild(keywordsMeta);
      }
      keywordsMeta.setAttribute('content', keywords);
    }

    // Canonical URL - critical for SEO
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink && canonical) {
      canonicalLink.setAttribute('href', canonical);
    }

    // Robots meta for staging safeguard
    const currentHost = window.location.hostname;
    const isProduction = (
      currentHost === 'cosmos-wave.com' || 
      currentHost === 'www.cosmos-wave.com' ||
      (!currentHost.includes('lovable.app') && !currentHost.includes('localhost'))
    );
    
    if (noindex || !isProduction) {
      let robotsMeta = document.querySelector('meta[name="robots"]');
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.setAttribute('name', 'robots');
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute('content', 'noindex,nofollow');
    }

    // Open Graph Protocol for social sharing
    const ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) ogType.setAttribute('content', pageType);

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);

    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', description);

    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl && canonical) ogUrl.setAttribute('content', canonical);

    const ogImageMeta = document.querySelector('meta[property="og:image"]');
    const defaultImage = ogImage || 'https://www.cosmos-wave.com/og-home.jpg';
    if (ogImageMeta) ogImageMeta.setAttribute('content', defaultImage);

    // Twitter Card meta tags
    const twitterCard = document.querySelector('meta[name="twitter:card"]');
    if (twitterCard) twitterCard.setAttribute('content', 'summary_large_image');

    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);

    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', description);

    const twitterImage = document.querySelector('meta[name="twitter:image"]');
    if (twitterImage) twitterImage.setAttribute('content', defaultImage);

    // Clean up existing structured data scripts first
    const existingScripts = document.querySelectorAll('script[data-page-seo="true"]');
    existingScripts.forEach(script => script.remove());

    // Inject structured data schemas
    const schemasToInject = [];
    
    if (breadcrumbs) {
      schemasToInject.push(breadcrumbs);
    }
    
    if (structuredData.length > 0) {
      schemasToInject.push(...structuredData);
    }

    schemasToInject.forEach((schema, index) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-page-seo', 'true');
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    });

    // Cleanup function
    return () => {
      const cleanupScripts = document.querySelectorAll('script[data-page-seo="true"]');
      cleanupScripts.forEach(script => script.remove());
    };
  }, [title, description, canonical, ogImage, keywords, noindex, pageType, structuredData, breadcrumbs]);

  return null;
}